"use client";
import { useCallback, useEffect, useState } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";
import * as React from "react";

import {
  SessionEntity,
  TranscriptBlockEntity,
  ChatMessageEntity,
} from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import type {
  UseTranscriptParams,
  UseTranscriptReturn,
} from "~/modules/main/hooks/use-transcript/use-transcript.interface";

// Global state atoms
const currentQuestionAtom = atom<string>("");
const isSubmittingAtom = atom<boolean>(false);
const searchQueryAtom = atom<string>("");

export function useTranscript(params: UseTranscriptParams): UseTranscriptReturn {
  const { sessionId, autoLoad = true } = params;

  // Global state
  const [currentQuestion, setCurrentQuestion] = useAtom(currentQuestionAtom);
  const [isSubmitting, setIsSubmitting] = useAtom(isSubmittingAtom);
  const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Fetch session data - only enabled when autoLoad is true and sessionId is valid
  const {
    data: sessionData,
    isLoading: isLoadingSession,
    isError: isSessionError,
  } = trpcReactQueryApi.main.sessions.byId.useQuery(
    { id: sessionId },
    { enabled: autoLoad && !!sessionId },
  );

  // Fetch transcript blocks - only enabled when autoLoad is true and sessionId is valid
  const {
    data: transcriptData,
    isLoading: isLoadingTranscript,
    isError: isTranscriptError,
  } = trpcReactQueryApi.main.transcripts.bySessionId.useQuery(
    { sessionId },
    { enabled: autoLoad && !!sessionId },
  );

  // Fetch chat messages - only enabled when autoLoad is true and sessionId is valid
  const {
    data: chatMessagesData,
    isLoading: isLoadingChat,
    isError: isChatError,
  } = trpcReactQueryApi.main.chat.bySessionId.useQuery(
    { sessionId },
    { enabled: autoLoad && !!sessionId },
  );

  // Fetch quota - only enabled when autoLoad is true and sessionId is valid
  const {
    data: quotaData,
    isLoading: isLoadingQuota,
  } = trpcReactQueryApi.main.chat.getQuota.useQuery(
    { sessionId },
    { enabled: autoLoad && !!sessionId },
  );

  // Submit question mutation
  const submitQuestionMutation =
    trpcReactQueryApi.main.chat.submitQuestion.useMutation({
      onSuccess: async () => {
        toast.success("Question submitted successfully");
        await utils.main.chat.bySessionId.invalidate();
        await utils.main.chat.getQuota.invalidate();
        setCurrentQuestion("");
        setIsSubmitting(false);
      },
      onError: (error) => {
        toast.error(error.message ?? "Failed to submit question");
        setIsSubmitting(false);
      },
    });

  // Commands
  const formatTimestamp = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const jumpToTimestamp = useCallback(
    (seconds: number): void => {
      // 1) Find closest preceding block: last block with startSeconds <= seconds
      let targetId: string | null = null;
      const blocks = transcriptData ?? [];
      for (let i = blocks.length - 1; i >= 0; i -= 1) {
        const blk = blocks[i];
        if (blk && blk.startSeconds <= seconds) {
          targetId = blk.id;
          break;
        }
      }

      // 2) Scroll and highlight in transcript container if element exists
      if (targetId) {
        const container = document.querySelector<HTMLElement>(
          "[data-transcript-container]",
        );
        const target = container?.querySelector<HTMLElement>(
          `[data-block-id="${targetId}"]`,
        );
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
          // Brief highlight using inline style. Revert after 2s.
          const originalBg = target.style.backgroundColor;
          target.style.backgroundColor = "rgba(215, 38, 56, 0.1)"; // ruby tint
          window.setTimeout(() => {
            // Guard element still mounted
            if (target) target.style.backgroundColor = originalBg;
          }, 2000);
        }
      }

      // 3) Optional audio sync: page-scoped audio player
      const audio = document.querySelector<HTMLAudioElement>(
        "[data-audio-player]",
      );
      if (audio) {
        try {
          audio.currentTime = seconds;
          // DO NOT auto-play; respect existing play/pause state.
        } catch {
          // no-op if the element can't seek
        }
      }
    },
    [transcriptData],
  );

  const getTranscriptAsText = useCallback((): string => {
    if (!transcriptData) return "";
    return transcriptData
      .map((block) => `[${formatTimestamp(block.startSeconds)}] ${block.text}`)
      .join("\n\n");
  }, [transcriptData, formatTimestamp]);

  // Mutations
  const updateCurrentQuestion = useCallback(
    () => ({
      mutate: (question: string) => {
        setCurrentQuestion(question);
      },
      loading: false,
      error: null,
    }),
    [setCurrentQuestion],
  );

  const submitQuestion = useCallback(
    () => ({
      mutate: async (question: string) => {
        if (!question.trim()) {
          toast.error("Please enter a question");
          return;
        }
        if ((quotaData?.remaining ?? 0) <= 0) {
          toast.error("Message quota exceeded. Please wait before sending more messages.");
          return;
        }
        setIsSubmitting(true);
        await submitQuestionMutation.mutateAsync({
          sessionId,
          question: question.trim(),
        });
      },
      loading: isSubmitting,
      error: submitQuestionMutation.error?.message ?? null,
    }),
    [sessionId, quotaData, isSubmitting, submitQuestionMutation, setIsSubmitting],
  );

  const renameSession = useCallback(
    () => ({
      mutate: (_newTitle: string) => {
        toast.info("Rename session is not implemented yet.");
      },
      loading: false,
      error: null,
    }),
    [],
  );

  const deleteSession = useCallback(
    () => ({
      mutate: () => {
        toast.info("Delete session is not implemented yet.");
      },
      loading: false,
      error: null,
    }),
    [],
  );

  const downloadTranscript = useCallback(
    () => ({
      mutate: async () => {
        try {
          // Call server endpoint to get transcript content
          const result = await utils.main.transcripts.downloadAsText.fetch({
            sessionId,
          });
          
          // Create blob from server-provided content
          const blob = new Blob([result.content], { type: "text/plain;charset=utf-8" });
          const url = URL.createObjectURL(blob);
          
          // Trigger download with server-provided filename or fallback
          const a = document.createElement("a");
          a.href = url;
          a.download = result.filename ?? `transcript-${sessionId}.txt`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          toast.success("Transcript downloaded successfully");
        } catch (err) {
          toast.error("Failed to download transcript");
        }
      },
      loading: false,
      error: null,
    }),
    [utils, sessionId],
  );

  const reload = useCallback(
    () => ({
      mutate: async () => {
        try {
          await Promise.all([
            utils.main.sessions.byId.invalidate(),
            utils.main.transcripts.bySessionId.invalidate(),
            utils.main.chat.bySessionId.invalidate(),
            utils.main.chat.getQuota.invalidate(),
          ]);
          toast.success("Reloaded");
        } catch (_e) {
          toast.error("Failed to reload");
        }
      },
      loading: false,
      error: null,
    }),
    [utils],
  );

  const updateSearchQuery = useCallback(
    () => ({
      mutate: (query: string) => {
        setSearchQuery(query);
      },
      loading: false,
      error: null,
    }),
    [setSearchQuery],
  );

  const getFilteredTranscriptBlocks = useCallback((): TranscriptBlockEntity[] => {
    if (!transcriptData) return [];
    const q = (searchQuery ?? "").trim().toLowerCase();
    if (!q) return transcriptData;
    return transcriptData.filter((b: TranscriptBlockEntity) =>
      (b.text ?? "").toLowerCase().includes(q),
    );
  }, [transcriptData, searchQuery]);

  return {
    // State
    session: sessionData ?? null,
    transcriptBlocks: transcriptData ?? [],
    chatMessages: chatMessagesData ?? [],
    isLoading: isLoadingChat,
    isLoadingSession,
    isLoadingTranscript,
    isError: Boolean(isSessionError || isTranscriptError || isChatError || submitQuestionMutation.isError),
    searchQuery,
    isSubmittingChat: isSubmitting,
    submitError: submitQuestionMutation.error?.message ?? null,
    remainingQuota: quotaData?.remaining ?? 0,
    quotaLimit: quotaData?.limit ?? 25,

    // Queries
    getFilteredTranscriptBlocks,

    // Mutations
    updateSearchQuery,
    submitQuestion,
    renameSession,
    deleteSession,
    downloadTranscript,
    reload,

    // Commands
    formatTimestamp,
    jumpToTimestamp,
  };
}